import { HelpCircle } from "lucide-react";
import type * as React from "react";
import { useState } from "react";
import { Controller, get, useFormContext } from "react-hook-form";
import { strings } from "@/I18n";
import { cn } from "@/lib/utils";
import { TEST_IDS } from "@/v2/constants/testIds";
import { Input } from "@/v2/ui/primitives/input";
import { Label } from "@/v2/ui/primitives/label";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/v2/ui/primitives/tooltip";

export interface FormInputProps {
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  type?: string;
  className?: string;
  /** Prefix shown inside input (e.g., currency symbol) */
  inputPrefix?: string;
  /** Info tooltip text */
  info?: string;
  /** Convert value to/from cents (multiply/divide by 100) */
  convertType?: "cent";
}

export const FormInput: React.FC<FormInputProps> = ({
  name,
  label,
  placeholder,
  required = false,
  disabled = false,
  type = "text",
  className,
  inputPrefix,
  info,
  convertType,
}) => {
  // Keep unfinished decimal text ("0.", "0.10") while the form stores minor units.
  const [moneyDraft, setMoneyDraft] = useState<{
    text: string;
    value: number | null;
  } | null>(null);
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const error = get(errors, name);
  const errorMessage = error?.message as string | undefined;

  return (
    <div
      className={cn("tw-space-y-2 tw-min-w-0", className)}
      data-testid={TEST_IDS.formField(name)}
    >
      {(label || info) && (
        <div className="tw-flex tw-items-center tw-gap-1">
          {label && (
            <Label htmlFor={name}>
              {label}
              {required && (
                <span className="tw-text-destructive tw-ml-1">*</span>
              )}
            </Label>
          )}
          {info && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild={true}>
                  <button
                    type="button"
                    aria-label={info}
                    className="tw-p-1 tw-shrink-0 tw-cursor-help tw-text-muted-foreground"
                  >
                    <HelpCircle className="tw-h-4 tw-w-4 tw-text-muted-foreground" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <p className="tw-text-sm tw-max-w-[240px] tw-whitespace-normal">
                    {info}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}
      <Controller
        name={name}
        control={control}
        rules={{
          required: required
            ? strings("validation.fieldRequired", {
                field: label || strings("form.thisField"),
              })
            : false,
        }}
        render={({ field }) => (
          <div
            className={cn(
              "tw-flex tw-items-center tw-rounded-lg tw-border tw-border-input tw-bg-background tw-transition-colors focus-within:tw-border-primary focus-within:tw-ring-2 focus-within:tw-ring-primary/15",
              disabled && "tw-opacity-50",
              error &&
                "tw-border-destructive focus-within:tw-border-destructive"
            )}
          >
            {inputPrefix && (
              <div
                data-input-prefix=""
                id={`${name}-currency`}
                className="tw-self-stretch tw-flex tw-items-center tw-px-3 tw-border-r tw-border-input tw-text-muted-foreground tw-text-xs tw-font-medium tw-shrink-0"
              >
                {inputPrefix}
              </div>
            )}
            <Input
              {...field}
              id={name}
              type={convertType === "cent" ? "text" : type}
              inputMode={convertType === "cent" ? "decimal" : undefined}
              aria-invalid={Boolean(error)}
              aria-describedby={
                [
                  inputPrefix && `${name}-currency`,
                  errorMessage && `${name}-error`,
                ]
                  .filter(Boolean)
                  .join(" ") || undefined
              }
              placeholder={placeholder}
              disabled={disabled}
              value={
                convertType === "cent"
                  ? moneyDraft && Object.is(moneyDraft.value, field.value)
                    ? moneyDraft.text
                    : typeof field.value === "number" &&
                        Number.isFinite(field.value)
                      ? (field.value / 100).toFixed(2)
                      : ""
                  : (field.value ?? "")
              }
              onChange={(e) => {
                if (convertType === "cent") {
                  const text = e.target.value;
                  if (!/^-?\d*([.,]\d{0,2})?$/.test(text)) return;
                  const minorUnits = Math.round(
                    Number(text.replace(",", ".")) * 100
                  );
                  const value =
                    text === "" || !Number.isFinite(minorUnits)
                      ? null
                      : minorUnits;
                  setMoneyDraft({ text, value });
                  field.onChange(value);
                } else if (type === "number") {
                  const numValue =
                    e.target.value === "" ? "" : parseFloat(e.target.value);
                  field.onChange(
                    Number.isNaN(numValue as number) ? e.target.value : numValue
                  );
                } else {
                  field.onChange(e.target.value);
                }
              }}
              onBlur={() => {
                setMoneyDraft(null);
                field.onBlur();
              }}
              className={cn(
                "tw-min-w-0 tw-flex-1 tw-border-0 tw-bg-transparent tw-text-foreground tw-shadow-none focus-visible:tw-ring-0 disabled:tw-opacity-100",
                convertType === "cent" && "tw-tabular-nums"
              )}
            />
          </div>
        )}
      />
      {errorMessage && (
        <p
          id={`${name}-error`}
          className="tw-text-sm tw-text-destructive tw-mt-1"
          data-testid={TEST_IDS.formError(name)}
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
};
